import { InternalOrder } from '@abovo/shared-models';
import { primaryKpi } from '@abovo/webvis-data-access';
import { computed, inject, Injectable, signal } from '@angular/core';
import { FormArray, FormBuilder } from '@angular/forms';
import { ActualKeyPerformanceIndicatorInternal, IntermediateReportRow, KpiForm, KpiFormGroup } from '../models/intermediate-report.models';

/**
 * Simple service to rembember the entered KPI data between display switches
 */
@Injectable()
export class FormHelperService {
   private readonly formBuilder = inject(FormBuilder);

   /** Set in the report grid container */
   readonly disableEdit = signal(false);

   /** Set in the intermediate report grid. Used for the export */
   readonly readonly = signal(false);
   readonly editingDisabled = computed(() => this.disableEdit() || this.readonly());

   /**
    * Builds a formgroup for the actuals value and investment with the kpi slug as key. The `orders`
    * are the real orders this row's values are saved onto on submit (`[order]` for a normal row, every
    * bundled order for a bundle row); they ride along on the returned form so the save step can split
    * the entered values back across them.
    */
   buildKpiForm(kpis: Array<ActualKeyPerformanceIndicatorInternal>, orders: Array<InternalOrder>, order?: IntermediateReportRow): KpiForm {
      // First sort the kpis so the primary kpi is the first in the list for the kpi_investment
      const sortedKpis = kpis.toSorted((kpiA, kpiB) => Number(!!kpiB.is_primary_kpi) - Number(!!kpiA.is_primary_kpi));
      const formGroup = this.formBuilder.array(sortedKpis.map((kpi) => this.buildKpiFormGroup(kpi, order)));
      return { formGroup, orders };
   }

   /**
    * The planned value an order contributes to a given KPI slug — its primary `kpi_value` when the slug
    * matches, otherwise the matching secondary KPI's value (the same lookup {@link buildKpiFormGroup}
    * uses for the read-only `orderValue` column). Used as the weight when distributing a bundle's
    * entered actuals back across its orders. Returns 0 when the order does not carry the slug.
    */
   plannedValueForSlug(order: InternalOrder, slug: string): number {
      const primaryOrderKpi = primaryKpi(order);
      if (primaryOrderKpi?.kpi?.slug === slug) {
         return primaryOrderKpi.kpi_value?.value ?? 0;
      }
      return order.secondary_kpis?.find((secondaryKpi) => secondaryKpi.kpi?.slug === slug)?.kpi_value ?? 0;
   }

   /**
    * Whether an order carries the given KPI slug in its plan, as primary or secondary. Distinct from
    * {@link plannedValueForSlug} because a KPI can be present with a 0 (or non-fillable) planned value —
    * presence decides whether a bundle order should get a record, the planned value only weights the split.
    */
   orderHasKpi(order: InternalOrder, slug: string): boolean {
      return primaryKpi(order)?.kpi?.slug === slug || (order.secondary_kpis ?? []).some((secondaryKpi) => secondaryKpi.kpi?.slug === slug);
   }

   /**
    * Splits `total` across `weights` proportionally, returning one part per weight. The remainder of
    * the rounding is folded into the first (head) part so the parts always sum back to `total` exactly.
    * When every weight is 0 the total is spread evenly instead. A nullish `total` (e.g. a secondary
    * KPI's investment) yields `null` for every part so no value is invented.
    */
   distributeProportionally(total: number | null | undefined, weights: number[]): Array<number | null> {
      if (total === null || total === undefined) {
         return weights.map(() => null);
      }
      const weightSum = weights.reduce((accumulator, weight) => accumulator + weight, 0);
      const effectiveWeights = weightSum > 0 ? weights : weights.map(() => 1);
      const effectiveSum = weightSum > 0 ? weightSum : effectiveWeights.length;
      const parts = effectiveWeights.map((weight) => Math.round(((total * weight) / effectiveSum) * 100) / 100);
      // Fold the rounding remainder into the head part so the parts sum back to `total` exactly.
      const tailSum = parts.slice(1).reduce((accumulator, part) => accumulator + part, 0);
      parts[0] = Math.round((total - tailSum) * 100) / 100;
      return parts;
   }

   buildKpiFormGroup(actualKpi: ActualKeyPerformanceIndicatorInternal, order?: IntermediateReportRow) {
      const primaryOrderKpi = order ? primaryKpi(order) : null;
      const secondaryOrderKpi = order?.secondary_kpis?.find((okpi) => okpi.kpi?.slug === actualKpi.kpi.slug);
      const form = this.formBuilder.group({
         id: this.formBuilder.nonNullable.control(actualKpi.id),
         kpi: this.formBuilder.nonNullable.control(actualKpi.kpi),
         is_primary_kpi: actualKpi.is_primary_kpi,
         kpi_value: actualKpi.kpi_value,
         kpi_investment: actualKpi.kpi_investment,
         rate: actualKpi.rate,
         // Value is never entered by the user in this report, the value is for display purposes only
         orderValue: {
            value: primaryOrderKpi?.kpi?.slug === actualKpi.kpi.slug ? primaryOrderKpi?.kpi_value?.value : secondaryOrderKpi?.kpi_value,
            disabled: true,
         },
      });
      if (this.editingDisabled()) {
         form.disable();
      }
      return form;
   }

   /**
    * Will return true if the investment and all values of the formarray are filled in, in that case the template will show an icon instead of employee images
    */
   isReportStatusDone(formGroup: FormArray<KpiFormGroup>): boolean {
      // This is not the most efficient solution, but it is the simplest and I chose that over adding more code to the already complicated grid component
      return formGroup.getRawValue().every((data, index) => (index > 0 || typeof data.kpi_investment === 'number') && typeof data.kpi_value === 'number');
   }
}
